import { useCallback, useMemo } from 'react';
import useLoanMutation from 'hooks/useEditLoan';
import groupBy from 'lodash/groupBy';
import { toast } from 'react-toastify';
import LoanVariablesForm from 'components/loan/LoanVariablesForm';
import { useIntl } from 'react-intl';
import useAdminLoan from 'hooks/useAdminLoan';
import LoadingOverlay from 'components/common/LoadingOverlay';
import { pick } from 'lodash';
import type { FormikHelpers } from 'formik';
import type { TFormValues } from 'components/inputs/types';
import type { TAdminLoan } from 'hooks/useAdminLoan';
import requireValue from 'utils/requireValue';
import { messageOf } from 'utils/error';

interface IInterestRateInput {
  sk1: number;
  period: number;
  interest_rate: unknown;
}

interface IFixedInterestRateInput {
  key: string;
  value: unknown;
}

function formatInterestRateValues(formData: TFormValues) {
  const interestRates: IInterestRateInput[] = [];
  return Object.keys(formData)
    .filter((key) => key.includes(`interestRate`))
    .reduce((array, key) => {
      // The string divided by 100 is its number divided by 100.
      const sk1 = parseFloat(String(Number(key.split('_')[1]) / 100));
      const period = parseFloat(key.split('_')[2]);
      array.push({ sk1, period, interest_rate: formData[key] });
      return array;
    }, interestRates);
}

function formatFixedInterestRateValues(formData: TFormValues) {
  const fixedInterestRates: IFixedInterestRateInput[] = [];
  return Object.keys(formData)
    .filter((key) => key.includes(`fixedInterestRate`))
    .reduce((array, key) => {
      array.push({ key: key.split('fixedInterestRate_')[1], value: formData[key] });
      return array;
    }, fixedInterestRates);
}

const whiteListFormData = [
  'sk1Factor',
  'loanCosts',
  'loanCostsFix',
  'ownFundsRatio',
  'maxLoanRateRatio',
  'interestBandwith',
];

const useChangeLoanHandler = () => {
  const changeLoan = useLoanMutation();
  const { formatMessage: t } = useIntl();

  const handleSubmit = useCallback(
    async (formData: TFormValues, { setSubmitting }: FormikHelpers<TFormValues>) => {
      try {
        const { euribor_6m } = formData;
        const interestRates = formatInterestRateValues(formData);
        const fixedInterestRate = formatFixedInterestRateValues(formData);
        const variableInterestRate = { euribor_6m };
        const whiteListFormValues = pick(formData, whiteListFormData);

        await changeLoan({
          ...whiteListFormValues,
          fixedInterestRate,
          variableInterestRate,
          interestRates,
        });
        toast.success(t({ id: 'loan.changes_applied' }));
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [changeLoan, t],
  );
  return handleSubmit;
};

function getInitialValues(loan: TAdminLoan | null): TFormValues {
  if (!loan) return {};

  const { variableInterestRate, interestRates, fixedInterestRate, ...rest } = loan;
  // Reading a missing rate object, list or list item throws, as it did.
  const { euribor_6m } = requireValue(variableInterestRate, 'euribor_6m');

  const noFixedRates: Record<string, number | null> = {};
  const fixedRates = requireValue(fixedInterestRate, 'reduce').reduce((result, rate) => {
    const { key, value } = requireValue(rate, 'key');
    return { ...result, [`fixedInterestRate_${key}`]: value };
  }, noFixedRates);

  // Bug: the untyped code sorted the rates with orderBy(interestRates, ['sk1', 'period'], ['desc', 'desc']) and
  // then grouped the unsorted ones; the unused (side-effect free) sort is left out.
  const groupedInterestRates = groupBy(interestRates, 'sk1');

  const noRates: Record<string, number | null> = {};
  const formattedInterestRates = Object.entries(groupedInterestRates).reduce(
    (result, [key, value]) => {
      const interest = value.reduce((res, rate) => {
        const { period, interest_rate } = requireValue(rate, 'period');
        return { ...res, [`interestRate_${Number(key) * 100}_${period}`]: interest_rate };
      }, noRates);
      return { ...result, ...interest };
    },
    noRates,
  );

  return { euribor_6m, ...fixedRates, ...formattedInterestRates, ...rest };
}

const Loan = () => {
  const onSubmit = useChangeLoanHandler();
  const [loan, loading] = useAdminLoan();
  const initialValues = useMemo(() => getInitialValues(loan), [loan]);

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <LoanVariablesForm
      {...{ onSubmit, initialValues }}
      lastUpdatedAt={loan?.last_updated_at}
      lastUpdatedBy={loan?.last_updated_by}
    />
  );
};

export default Loan;
